/**
 * International Auctions — the lot profile (BXS-3627 · F1.4).
 *
 * Route: `/:locale/international-auctions/:house/:sale/:lot`
 *
 * Four things about this page are load-bearing, and each is a deliberate
 * decision rather than an implementation detail:
 *
 *  1. NO MEDIA IS THE NORMAL CASE. Neither surveyed Tattersalls sale published a
 *     single photograph (design Appendix B.4), so `hasNoMedia()` drives a
 *     first-class placeholder — the house monogram on the placeholder ground —
 *     and never a broken image, an empty grey box or a "0 photos" count.
 *
 *  2. THE PRICE NEVER IMPLIES THE OUTCOME. Tattersalls publish `Lot Not Sold`
 *     beside `4,500 gns`: that figure is the bid the lot failed to make, not a
 *     sale. The outcome is therefore stated FIRST, on its own line, as a chip;
 *     the price is a separately-labelled published figure underneath, and its
 *     ink is only the "money changed hands" green for `sold` / `private_sale`.
 *     The `purchaser` row is suppressed for `not_sold`, because the house writes
 *     "Lot Not Sold" into that column and rendering it as a buyer would be a
 *     plain lie. Where `purchaser_raw` differs from `purchaser` the raw string
 *     is shown verbatim under `lot.as_published` — the house conflates status,
 *     buyer and private-sale marker in one cell and we keep the original rather
 *     than lose whatever parsing drops.
 *
 *  3. TABS ONLY EXIST WHEN THEY HAVE CONTENT. A tab with nothing behind it is
 *     absent, not present-and-empty.
 *
 *  4. THE OUTBOUND CTA NEVER LINKS STRAIGHT TO THE HOUSE. It points at the
 *     server-side redirect, `{environment.outboundUrl}/{token}`, which writes the
 *     `external_events` row, appends UTM and 302s onward (design §8.3). That
 *     click is the single most commercially important number in this module, and
 *     a server-side hop is the only way to count it exactly — a beacon fired on a
 *     link that is navigating away is the least reliable event in analytics. The
 *     destination is NOT passed along: it lives on the lot row in our database,
 *     and putting it in the query string would invite somebody to rewrite it.
 *
 * `getLot()` returns `null` for an unknown lot rather than throwing, so the
 * not-found state is a render, not an error handler — and it is gated on
 * `isLoading` so it can never flash while the request is in flight.
 *
 * Design:  docs/external-cataloge/phase-1/international-auctions-high-level.md
 * Mockup:  docs/external-cataloge/…/Horse Profile.dc.html
 */
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { SafeUrlPipe } from 'src/app/Shared/_pipes/safe-url.pipe';
import { MatDialog } from '@angular/material/dialog';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';
import { ImageLightboxComponent } from 'src/app/Shared/components/image-lightbox/image-lightbox.component';
import { LanguageNavigationService } from 'src/app/Core/locale/language-navigation.service';
import {
  LotJumpComponent,
  LotJumpEntry,
} from 'src/app/Shared/components/lot-jump/lot-jump.component';
import { BuyerAuthService } from 'src/app/app-modules/buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';

import { InternationalAuctionsService } from '../../../_services/international-auctions.service';
import { InternationalMyListService } from '../../../_services/international-my-list.service';
import { InternationalFavouriteButtonComponent } from '../_shared/favourite-button/international-favourite-button.component';
import { InternationalLotNotesComponent } from '../_shared/lot-notes/international-lot-notes.component';
import {
  ExternalLotDocument,
  ExternalLotDocumentFile,
  ExternalLotStatus,
  ExternalLotVideo,
  ExternalLotView,
  SupportedLocale,
  buildGoToken,
  formatLotPrice,
  hasNoMedia,
  parseIsoDate,
  resolveTranslation,
} from '../_mock/international-auctions.models';
import { HouseBrand, resolveHouseBrand } from '../_shared/house-brand';

/* ------------------------------------------------------------------ *
 * View models — built once per lot / locale, never derived in a getter
 * that change detection would re-run on every tick.
 * ------------------------------------------------------------------ */

/** One row of the horse-details table. Empty values never get here. */
export interface LotDetailRow {
  /** An i18n key, for the fields we model. */
  labelKey: string | null;
  /** Raw house-supplied label, for the `raw_details` passthrough. */
  labelText: string | null;
  value: string;
  /**
   * An i18n key for the VALUE, used by the one field whose emptiness is itself
   * the information: a lot with no declarations reads "None declared", which is
   * a sentence in the visitor's language rather than house-supplied data.
   */
  valueKey: string | null;
  /** Lot numbers and codes read as data, so they get the mono face. */
  mono: boolean;
}

/** A photo or a video, unified so the stage and the strip share one list. */
export interface LotMediaItem {
  id: string;
  kind: 'photo' | 'video';
  /** Full-size photo URL, or the video poster where the house supplied one. */
  url: string;
  thumbUrl: string;
  caption: string;
  /**
   * `width / height` of the photograph, or `null` when it is unknown — an
   * unmirrored row, a file missing from the bucket, or a video, whose poster
   * frame is always shown in a 16:9 player by construction.
   *
   * The stage and the thumbnail are sized from this, so a portrait shot gets a
   * portrait box instead of losing its head and its legs to a centred crop
   * (18 Aug review: "they show the whole horse, we show a slice"). It rides on
   * the payload rather than being measured from the loaded image on purpose —
   * a box that resizes once the bytes land is a layout shift on every click of
   * the strip.
   */
  ratio: number | null;
  /** Populated for `kind === 'video'` only. */
  video: ExternalLotVideo | null;
}

/** A tab that has something behind it. Tabs with nothing are never built. */
export interface LotTab {
  id: 'pedigree' | 'video' | 'documents' | 'updates';
  labelKey: string;
}

/**
 * What the browser can render inline. `other` is everything a viewer would
 * only show a download prompt for — a Word file, a spreadsheet, a zip.
 */
export type LotDocumentKind = 'pdf' | 'image' | 'other';

/**
 * One language's file for a document.
 *
 * A translated document is ONE row holding two PDFs, not two rows — the
 * category, the date and the access level are properties of the document, and
 * splitting it would make a visitor choose between two things that are the same
 * thing. Only the bytes differ, which is exactly what this carries.
 */
export interface LotDocumentVariant {
  locale: SupportedLocale;
  /** "Original" for the house's own file, "العربية" for ours. */
  labelKey: string;
  fileName: string;
  fileUrl: string;
  kind: LotDocumentKind;
  sizeLabel: string;
}

export interface LotDocumentRow {
  id: number;
  category: string;
  fileName: string;
  fileUrl: string;
  dateLabel: string;
  sizeLabel: string;
  /** Decides whether the row offers a viewer at all, and which one. */
  kind: LotDocumentKind;
  /**
   * One entry per language that actually holds a file, English first. A single
   * entry means no toggle: offering a language we do not hold is worse than
   * offering none.
   */
  variants: LotDocumentVariant[];
  /** The variant the four fields above are currently mirroring. */
  activeLocale: SupportedLocale;
  /**
   * The access type. `ExternalDocumentLock` carries three values, but an
   * International Auctions document is only ever one of TWO things to a
   * visitor: free to download, or behind a sign-in. `accepted_buyer` is a
   * BidXSell-native concept — a partner house has no notion of our approved
   * buyers — so it collapses into the same locked bucket rather than promising
   * a third state this module cannot honour.
   */
  isLocked: boolean;
  /** i18n key for the access chip: free, or sign-in required. */
  accessKey: string;
  /**
   * True for a document that opens the house's own system rather than
   * returning a file. It is still a document — it just cannot be previewed
   * and is not a download, so both of its labels change.
   */
  isLink: boolean;
  /** i18n key for the action when the visitor may have it: open, or download. */
  actionKey: string;
  /** i18n key for the action when they must sign in first. */
  lockedActionKey: string;
}

export interface LotUpdateRow {
  id: number;
  body: string;
  /** Pre-built so the template hands `translate` a stable object reference. */
  params: { date: string };
}

/* ------------------------------------------------------------------ *
 * Formatting helpers
 * ------------------------------------------------------------------ */

/** `SupportedLocale` → the BCP-47 tag `Intl` should format with. */
const INTL_LOCALES: Record<SupportedLocale, string> = {
  en: 'en-GB',
  ar: 'ar-EG',
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
};

/**
 * The closing time carries its zone name. A timed sale closes lots two minutes
 * apart, so "11:00" without a zone is a number a buyer in Riyadh cannot act on.
 */
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

/**
 * `Intl` formatters are expensive to build, so they are memoised for the life
 * of the bundle. Both are pure functions of their key and therefore safe to
 * share across SSR requests.
 */
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const DATE_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: string): Intl.DateTimeFormat {
  let formatter = DATE_FORMATTERS.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, DATE_OPTIONS);
    DATE_FORMATTERS.set(locale, formatter);
  }
  return formatter;
}

/**
 * The sale's own zone wherever the house states one — a Newmarket sale closes
 * at 11:00 BST and saying so is the whole point. An unknown or malformed zone
 * throws rather than degrading, so UTC is the fallback.
 */
function dateTimeFormatter(locale: string, timeZone: string): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}`;
  let formatter = DATE_TIME_FORMATTERS.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, {
        ...DATE_TIME_OPTIONS,
        timeZone,
      });
    } catch {
      formatter = new Intl.DateTimeFormat(locale, {
        ...DATE_TIME_OPTIONS,
        timeZone: 'UTC',
      });
    }
    DATE_TIME_FORMATTERS.set(key, formatter);
  }
  return formatter;
}

/**
 * Parses `bidding_ends_at`.
 *
 * A bare `2026-08-11T11:00:00` is LOCAL time to `Date.parse`, which would make
 * the server render one closing time and the browser another. Anything without
 * an explicit zone is therefore read as UTC, so both agree.
 */
function parseIsoDateTime(value: string): number {
  const text = value.trim().replace(' ', 'T');
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  return Date.parse(zoned ? text : `${text}Z`);
}

/**
 * The house categories whose documents back the Pedigree tab.
 *
 * More than one name, because the houses do not agree on one. Tattersalls
 * publishes the catalogue page and that page IS the pedigree — the family table
 * is printed on it — while the importer files its own scans under "Pedigree",
 * and the platform's original seller categories were seeded as "Pedigrees".
 * All three are the same thing to a visitor, so all three open the same tab.
 *
 * Matching on the English name is deliberate: `horse_document_categories` has
 * no slug, and its ids are auto-increment values that differ per environment.
 */
const PEDIGREE_CATEGORIES = new Set(['pedigree', 'pedigrees', 'catalogue page']);

/**
 * Toggle order, and the only two languages a document can hold. English leads
 * because it is the house's own file — the toggle reads "Original / العربية",
 * not "English / Arabic": the first button is the document as published.
 */
const DOCUMENT_LOCALES: SupportedLocale[] = ['en', 'ar'];

const DOCUMENT_LOCALE_LABEL_KEYS: Record<SupportedLocale, string> = {
  en: 'international_auctions.lot.doc_original',
  ar: 'international_auctions.lot.doc_arabic',
};

/**
 * How far the media stage will follow a photograph's own shape.
 *
 * `0.62` is just below 2:3 (0.667), the tallest shape the houses actually
 * publish in quantity, so every ordinary portrait is honoured exactly and only
 * the outliers letterbox. `2.0` does the same at the other end for panoramas.
 * `1.5` is the box the page had before any of this, and is what a video, an
 * empty stage and an unmeasured photograph still get.
 */
const STAGE_MIN_RATIO = 0.62;
const STAGE_MAX_RATIO = 2;
const STAGE_DEFAULT_RATIO = 1.5;

/** Lot outcome → the modifier class the price block wears. */
const STATUS_MODIFIERS: Record<ExternalLotStatus, string> = {
  catalogued: 'is-catalogued',
  withdrawn: 'is-withdrawn',
  sold: 'is-sold',
  not_sold: 'is-not-sold',
  private_sale: 'is-private',
};

@Component({
  selector: 'app-international-auctions-lot',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    SafeUrlPipe,
    LotJumpComponent,
    InternationalFavouriteButtonComponent,
    InternationalLotNotesComponent,
  ],
  templateUrl: './international-auctions-lot.component.html',
  styleUrl: './international-auctions-lot.component.scss',
})
export class InternationalAuctionsLotComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly languageNavigation = inject(LanguageNavigationService);
  private readonly dialog = inject(MatDialog);
  private readonly auctions = inject(InternationalAuctionsService);
  private readonly myList = inject(InternationalMyListService);
  private readonly localeService = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  /** Read for one thing only: whether a locked document can be downloaded. */
  private readonly auth = inject(BuyerAuthService);
  private readonly isBrowser: boolean;

  /* --- Request state ----------------------------------------------------- */

  isLoading = true;
  houseSlug = '';
  saleSlug = '';
  lotNumber = '';
  lot: ExternalLotView | null = null;

  /**
   * The lot's DATABASE id, which is what the shortlist endpoints address — the
   * route carries a lot NUMBER, and `26A` is not a key. `null` until the lot
   * has loaded, which is why the star and the notes panel are both behind an
   * `@if` in the template.
   */
  get lotId(): number | null {
    return this.lot?.id ?? null;
  }
  locale: SupportedLocale = 'en';

  /* --- Header ------------------------------------------------------------ */

  /** `Hip 101` for Fasig-Tipton, `Lot 101` for everyone else — house DATA. */
  lotPill = '';
  /** `display_title`, or the pill when the house published nothing (lot 3). */
  title = '';
  houseName = '';
  saleName = '';
  saleMeta = '';
  /** The band's four colours. Rebuilt once per lot, not per change detection. */
  brand: HouseBrand = resolveHouseBrand(null);
  brandColor = this.brand.color;
  brandContrast = this.brand.ink;
  monogram = '';
  logoUrl = '';
  /** `{ house: 'Tattersalls' }` for `lot.bid_on`, built once per lot. */
  bidOnParams: { house: string } = { house: '' };

  /** The pedigree line under the title, assembled from house data only. */
  sireName = '';
  damName = '';
  sireOfDamName = '';

  /**
   * Timeform's rating. Its own element rather than a table row: on a timed sale
   * it is a filter, a sort key and the first thing a buyer scans for, and a
   * number that carries that much weight does not belong in row eleven.
   */
  timeformRating = '';

  /* --- Routing ----------------------------------------------------------- */

  readonly indexLink: unknown[] = ['/international-auctions'];
  houseLink: unknown[] = ['/international-auctions'];
  saleLink: unknown[] = ['/international-auctions'];
  previousLink: unknown[] | null = null;
  nextLink: unknown[] | null = null;

  /* --- Jump to a lot number ---------------------------------------------- *
   * Prev/Next walks a 173-lot Tattersalls catalogue one page at a time, and
   * Keeneland September — the next house in the queue — catalogues around
   * 4,500. The box beside them takes a number (CEO review, 18 Aug 2026).
   * ----------------------------------------------------------------------- */

  /**
   * `null` until the visitor focuses the box. Nothing is fetched before that:
   * a visitor who never uses it never pays for the index.
   */
  lotJumpEntries: LotJumpEntry[] | null = null;
  lotJumpLoading = false;
  /** The house's own word — `Hip` at Fasig-Tipton. Falls back to ours. */
  lotJumpLabel = '';
  private lotJumpSub: Subscription | null = null;

  /**
   * The logged redirect. NEVER the house's URL directly — that click is the
   * module's headline number and only the server can count it exactly (§8.3).
   */
  goHref = '';

  /* --- Media ------------------------------------------------------------- */

  /** True when the house published neither a photo nor a video. Normal. */
  noMedia = true;
  media: LotMediaItem[] = [];
  activeMediaIndex = 0;
  /** Video ids the visitor has clicked to load. Nothing embeds unasked. */
  private readonly playing = new Set<number>();

  /**
   * Document id → the language the visitor picked for it. Held here rather than
   * on the row, because the rows are rebuilt on every language switch and a
   * deliberate choice must survive that: asking for the Arabic pedigree and
   * then switching the site to English should not silently swap the PDF back.
   */
  private readonly documentLocale = new Map<number, SupportedLocale>();

  /* --- Body -------------------------------------------------------------- */

  /**
   * One label/value pair per row, in catalogue order. How MANY pairs share a
   * line is decided by the stylesheet, not here: the details card is now the
   * full width of the page, so the column count has to answer to the viewport
   * rather than to a number chosen in TypeScript.
   */
  detailRows: LotDetailRow[] = [];
  /**
   * The house's own prose, kept OUT of `detailRows` and given the full width of
   * the card. It is the one field that is a paragraph rather than a value, and
   * a paragraph in a quarter-width table cell is what forced the old layout
   * tall and narrow in the first place.
   */
  descriptionText = '';
  tabs: LotTab[] = [];
  activeTabId: LotTab['id'] | null = null;

  /**
   * Drives the action on a locked document only. Read once on the browser and
   * then kept live, because signing in through the header popup must swap
   * "Sign in to download" for a real download without a reload.
   */
  isSignedIn = false;
  /** Where a locked document sends a signed-out visitor. */
  readonly signInLink = ['/buyer', 'auth', 'login'];

  pedigreeDocuments: LotDocumentRow[] = [];
  otherDocuments: LotDocumentRow[] = [];
  updates: LotUpdateRow[] = [];
  videos: ExternalLotVideo[] = [];

  /* --- Consignor --------------------------------------------------------- */

  /** `Consignor` / `Vendor` — house DATA, never an i18n concern. */
  consignorLabel = '';
  consignorName = '';
  contactName = '';
  contactPhone = '';
  contactEmail = '';

  /* --- Outcome ----------------------------------------------------------- */

  isWithdrawn = false;
  statusKey = '';
  statusModifier = 'is-catalogued';
  /**
   * When bidding on THIS lot closes, in the sale's own zone. On a timed sale
   * every lot has its own deadline, minutes apart, which makes this the single
   * most actionable fact on the page — so it sits with the outcome and the
   * price, not in the details table.
   */
  biddingEndsLabel = '';
  /** "Bidding ends" while it is ahead, "Bidding ended" once it is past. */
  biddingEndsKey = '';
  biddingHasEnded = false;
  /** `4,500 gns`, never `£4,500`. `null` when there is no price at all. */
  priceLabel: string | null = null;
  /** Only ever set for outcomes where money actually changed hands. */
  purchaserName = '';
  /** The house's own unparsed string, kept when parsing loses something. */
  purchaserRaw = '';

  private readonly subs = new Subscription();

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Subscribed rather than read once: switching language must re-resolve the
    // translated sale name, the document categories and the `Intl` dates
    // without a page reload.
    this.subs.add(
      this.localeService.languageCode$.subscribe((code) => {
        this.locale = code === 'ar' ? 'ar' : 'en';
        this.rebuild();
        this.cdr.markForCheck();
      })
    );

    // Browser only: the seed value reads storage, which is a no-op during SSR,
    // so asking on the server would render every locked document as signed-out
    // and then flip on hydration.
    if (this.isBrowser) {
      this.isSignedIn = this.auth.isLoggedInAndAuthorized();
      this.subs.add(
        this.auth.loggedIn_authorized$.subscribe((signedIn) => {
          this.isSignedIn = !!signedIn;
          this.cdr.markForCheck();
        })
      );
    }

    // `switchMap` so a fast prev → next → prev cannot land an older lot on the
    // page after a newer one.
    this.subs.add(
      this.route.paramMap
        .pipe(
          switchMap((params) => {
            this.houseSlug = params.get('house') ?? '';
            this.saleSlug = params.get('sale') ?? '';
            this.lotNumber = params.get('lot') ?? '';
            this.isLoading = true;
            this.lot = null;
            this.reset();
            return this.auctions.getLot(
              this.houseSlug,
              this.saleSlug,
              this.lotNumber
            );
          })
        )
        .subscribe((lot) => {
          this.lot = lot;
          this.isLoading = false;
          this.rebuild();

          // Whether this buyer has already starred or written on this lot.
          // A no-op for guests and during SSR — the service answers without
          // reaching the network in both cases.
          if (lot) {
            this.myList.loadStates([lot.id]).subscribe();
          }

          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.lotJumpSub?.unsubscribe();
  }

  /* ---------------------------------------------------------------- *
   * Jump to a lot number
   * ---------------------------------------------------------------- */

  /**
   * The box asks for its index on FIRST FOCUS, once. The service caches the
   * answer per sale, so walking the catalogue lot by lot costs one request for
   * the whole visit rather than one per page.
   *
   * Held outside `subs` because it survives the route change that `subs` does
   * not: the index belongs to the sale, and prev/next stays inside one.
   */
  onLotJumpLoad(): void {
    if (this.lotJumpEntries !== null || this.lotJumpLoading) {
      return;
    }

    this.lotJumpLoading = true;
    this.lotJumpSub?.unsubscribe();
    this.lotJumpSub = this.auctions
      .listLotNumbers(this.houseSlug, this.saleSlug)
      .subscribe((rows) => {
        this.lotJumpEntries = rows.map((row) => ({
          value: row.lot_number,
          label: row.title,
          // Tagged, NOT blocked. A withdrawn lot here has a real page that
          // says "This lot has been withdrawn from the sale", it is printed in
          // the catalogue the visitor is holding, and three of the first 173
          // Tattersalls lots are exactly this. Refusing to open it would hide
          // the answer the visitor came for.
          tagKey: row.status === 'withdrawn' ? 'lot_jump.tag_withdrawn' : undefined,
        }));
        this.lotJumpLoading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * A resolved lot number. The route is ALREADY keyed by lot number, so this is
   * the same navigation prev/next makes — the locale prefix is applied by hand
   * because `| localeLink` is a template pipe and this is a click handler.
   */
  onLotJump(entry: LotJumpEntry): void {
    const [root, ...rest] = this.saleLink as string[];
    this.router.navigate([
      this.languageNavigation.toLocalizedUrl(root),
      ...rest,
      entry.value,
    ]);
  }

  /* ---------------------------------------------------------------- *
   * Interaction — all of it pure state, so it works identically on the
   * server-rendered first paint and after hydration.
   * ---------------------------------------------------------------- */

  selectMedia(index: number): void {
    if (index >= 0 && index < this.media.length) {
      this.activeMediaIndex = index;
    }
  }

  selectTab(id: LotTab['id']): void {
    this.activeTabId = id;
  }

  /** Click-to-load: nothing is embedded from a third party until asked. */
  playVideo(video: ExternalLotVideo): void {
    this.playing.add(video.id);
  }

  isPlaying(video: ExternalLotVideo): boolean {
    return this.playing.has(video.id);
  }

  /**
   * Swaps which language of a document is on screen. The row is mutated rather
   * than rebuilt: everything else about the document — its category, its date,
   * its access level — is unchanged, and rebuilding the list would collapse the
   * open preview and scroll the visitor away from what they were reading.
   */
  selectDocumentLocale(row: LotDocumentRow, locale: SupportedLocale): void {
    this.documentLocale.set(row.id, locale);
    this.applyVariant(row, locale);
  }

  /**
   * Opens the platform's own lightbox — the same dialog the native horse
   * profile uses, so "see it bigger" behaves identically on both catalogues.
   *
   * It replaces a `window.open()` on the raw file, which sent the visitor off
   * the site into a bare image tab with no caption, no way back to the next
   * photograph, and a signed storage URL that expires.
   *
   * PHOTOS ONLY, and the index is recomputed against the photo list rather
   * than reused from `media`: `media` interleaves videos, whose lightbox path
   * expects an embeddable URL we do not have here. A video already plays in
   * place on the stage.
   */
  openPhoto(item: LotMediaItem): void {
    if (!this.isBrowser || item.kind !== 'photo' || !item.url) {
      return;
    }

    const photos = this.media.filter((entry) => entry.kind === 'photo');
    const index = Math.max(
      0,
      photos.findIndex((entry) => entry.id === item.id)
    );

    this.dialog.open(ImageLightboxComponent, {
      data: {
        src: photos[index]?.url,
        images: photos.map((entry) => ({ src: entry.url, type: 'image' })),
        currentIndex: index,
      },
      panelClass: ['custom-dialog-container', 'buyer-theme'],
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
  }

  get activeMedia(): LotMediaItem | null {
    return this.media[this.activeMediaIndex] ?? null;
  }

  /**
   * The CSS `aspect-ratio` for the stage: the shape of the photograph in it.
   *
   * A single fixed box cannot serve this catalogue. Of the first Tattersalls
   * sale's 377 photographs, 155 are portrait and 217 landscape — 3:4 and 4:3
   * are the two commonest shapes, near-perfect mirror images of each other —
   * so any one ratio crops or letterboxes about half the gallery. Sizing the
   * box per photograph is the only answer that shows every horse whole.
   *
   * CLAMPED, because a shape is not a licence: one lot's photograph is
   * 738x1600, which at the stage's width would be a box two screens tall.
   * Beyond the clamp the box stops following the image and `object-fit:
   * contain` letterboxes it instead — smaller, but still whole.
   *
   * Falls back to the original 3:2 for a video (which plays in its own frame),
   * for the no-media state, and for a photograph we could not measure.
   */
  get stageRatio(): number {
    const ratio = this.activeMedia?.ratio ?? null;

    if (ratio === null) {
      return STAGE_DEFAULT_RATIO;
    }

    return Math.min(STAGE_MAX_RATIO, Math.max(STAGE_MIN_RATIO, ratio));
  }

  /* ---------------------------------------------------------------- *
   * Building the view
   * ---------------------------------------------------------------- */

  /**
   * Clears everything derived, so a pending request never shows stale data.
   * The breadcrumb links are rebuilt from the ROUTE rather than left blank,
   * because the not-found state still has to offer a way back to the sale.
   */
  private reset(): void {
    this.houseLink = this.houseSlug
      ? ['/international-auctions', this.houseSlug]
      : ['/international-auctions'];
    this.saleLink =
      this.houseSlug && this.saleSlug
        ? ['/international-auctions', this.houseSlug, this.saleSlug]
        : this.houseLink;
    this.media = [];
    this.activeMediaIndex = 0;
    this.playing.clear();
    // A language choice belongs to the document it was made on, and the next
    // lot's documents are different documents.
    this.documentLocale.clear();
    this.detailRows = [];
    this.descriptionText = '';
    this.tabs = [];
    this.activeTabId = null;
    this.pedigreeDocuments = [];
    this.otherDocuments = [];
    this.updates = [];
    this.videos = [];
    this.noMedia = true;
    this.previousLink = null;
    this.nextLink = null;
    // NOT `lotJumpEntries`: the index belongs to the SALE, and prev/next keeps
    // the visitor inside one. Clearing it here would refetch the whole book on
    // every step through the catalogue, which is the cost the box exists to
    // avoid. `loadLotJumpIndex` re-keys on the slugs anyway.
    this.goHref = '';
    this.timeformRating = '';
    this.biddingEndsLabel = '';
    this.biddingEndsKey = '';
    this.biddingHasEnded = false;
  }

  private rebuild(): void {
    const lot = this.lot;
    if (!lot) {
      return;
    }

    const house = lot.sale?.house;
    const intlLocale = INTL_LOCALES[this.locale] ?? INTL_LOCALES.en;

    /* --- header --- */
    const lotLabel = (house?.lot_label ?? '').trim();
    this.lotPill = lotLabel
      ? `${lotLabel} ${lot.lot_number}`
      : String(lot.lot_number);
    this.title = (lot.display_title ?? '').trim() || this.lotPill;
    this.lotJumpLabel = lotLabel;
    this.houseName = house?.name ?? '';
    this.bidOnParams = { house: this.houseName };
    this.brand = resolveHouseBrand(house);
    this.brandColor = this.brand.color;
    this.brandContrast = this.brand.ink;
    this.logoUrl = (house?.logo_url ?? '').trim();
    this.monogram = this.buildMonogram(this.houseName);

    this.saleName = resolveTranslation(lot.sale?.name, this.locale) ?? '';
    this.saleMeta = this.buildSaleMeta(intlLocale);

    this.sireName = (lot.sire ?? '').trim();
    this.damName = (lot.dam ?? '').trim();
    this.sireOfDamName = (lot.sire_of_dam ?? '').trim();

    // `0` is a real Timeform rating and must render; only an absent one hides
    // the chip.
    this.timeformRating =
      typeof lot.timeform_rating === 'number'
        ? String(lot.timeform_rating)
        : '';

    /* --- routing --- */
    const houseSlug = (house?.slug ?? lot.sale?.house_slug ?? this.houseSlug) || '';
    const saleSlug = lot.sale?.slug ?? this.saleSlug;
    this.houseLink = ['/international-auctions', houseSlug];
    this.saleLink = ['/international-auctions', houseSlug, saleSlug];
    this.previousLink = lot.previous_lot_number
      ? [...this.saleLink, lot.previous_lot_number]
      : null;
    this.nextLink = lot.next_lot_number
      ? [...this.saleLink, lot.next_lot_number]
      : null;

    // `source_url` is read for its PRESENCE only — it is never sent anywhere.
    // The redirect resolves the destination from this same row and 404s when it
    // is blank, so a lot the house catalogued without a lot page of its own
    // gets no button rather than one that opens an error. Three of the 173 lots
    // in the first Tattersalls import arrive exactly like that.
    // `lang` is not decoration: the redirect lives on the API origin, so the
    // browser trims the `Referer` it sends to a bare `https://bidxsell.com/`
    // under the default cross-origin policy — measured, not assumed. Without
    // this parameter the ledger's locale dimension (§8.4) is null on every
    // single row, and there is no second source for it.
    const token = buildGoToken('lot', houseSlug, saleSlug, lot.lot_number);
    this.goHref = (lot.source_url ?? '').trim()
      ? `${environment.outboundUrl}/${encodeURIComponent(token)}?lang=${this.locale}`
      : '';

    /* --- media --- */
    this.noMedia = hasNoMedia(lot);
    this.media = this.buildMedia(lot);
    this.activeMediaIndex = Math.min(
      this.activeMediaIndex,
      Math.max(this.media.length - 1, 0)
    );

    /* --- outcome --- */
    this.isWithdrawn = lot.status === 'withdrawn';
    this.statusKey = `international_auctions.lot_status.${lot.status}`;
    this.statusModifier = STATUS_MODIFIERS[lot.status] ?? 'is-catalogued';
    this.priceLabel = formatLotPrice(lot, this.locale);
    this.buildBiddingDeadline(lot, intlLocale);

    // "Lot Not Sold" is what the house writes into the purchaser column when
    // nobody bought it. Rendering that as a buyer would be a lie, so the row
    // exists only for the two outcomes where money changed hands.
    const soldToSomebody =
      lot.status === 'sold' || lot.status === 'private_sale';
    this.purchaserName = soldToSomebody ? (lot.purchaser ?? '').trim() : '';

    const raw = (lot.purchaser_raw ?? '').trim();
    this.purchaserRaw = raw && raw !== (lot.purchaser ?? '').trim() ? raw : '';

    /* --- consignor --- */
    this.consignorLabel = (house?.consignor_label ?? '').trim();
    this.consignorName = (lot.consignor ?? '').trim();
    this.contactName = (lot.consignor_contact_name ?? '').trim();
    this.contactPhone = (lot.consignor_contact_phone ?? '').trim();
    this.contactEmail = (lot.consignor_contact_email ?? '').trim();

    /* --- body --- */
    this.detailRows = this.buildDetailRows(lot, lotLabel, intlLocale);
    this.descriptionText = (lot.description ?? '').trim();
    this.videos = lot.videos ?? [];
    this.pedigreeDocuments = this.buildDocuments(
      (lot.documents ?? []).filter((doc) => this.isPedigreeDocument(doc)),
      intlLocale
    );
    this.otherDocuments = this.buildDocuments(
      (lot.documents ?? []).filter((doc) => !this.isPedigreeDocument(doc)),
      intlLocale
    );
    this.updates = this.buildUpdates(lot, intlLocale);
    this.tabs = this.buildTabs();

    // Keep the visitor's tab across a language switch; fall back to the first
    // one that still exists.
    if (!this.tabs.some((tab) => tab.id === this.activeTabId)) {
      this.activeTabId = this.tabs.length > 0 ? this.tabs[0].id : null;
    }
  }

  /**
   * The lot's own closing time, rendered in the sale's zone.
   *
   * Whether it is ahead or behind is settled ONCE, here, rather than in a
   * getter change detection would re-run every tick: a page that silently
   * relabels itself mid-scroll is worse than one that is accurate as of the
   * moment it loaded, and the visitor is being sent to the house to bid in any
   * case.
   */
  private buildBiddingDeadline(lot: ExternalLotView, intlLocale: string): void {
    const raw = (lot.bidding_ends_at ?? '').trim();
    if (!raw) {
      return;
    }
    const at = parseIsoDateTime(raw);
    if (!Number.isFinite(at)) {
      return;
    }

    const zone = (lot.sale?.timezone ?? '').trim() || 'UTC';
    this.biddingEndsLabel = dateTimeFormatter(intlLocale, zone).format(at);
    this.biddingHasEnded = at <= Date.now();
    this.biddingEndsKey = this.biddingHasEnded
      ? 'international_auctions.lot.bidding_ended'
      : 'international_auctions.lot.bidding_ends';
  }

  /** "Fasig-Tipton" → "FT", "Tattersalls" → "T". Two letters is the ceiling. */
  private buildMonogram(name: string): string {
    const words = name.split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 0);
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
  }

  /** `24 – 26 Jul 2026 · Park Paddocks, Newmarket`. Either half may be absent. */
  private buildSaleMeta(intlLocale: string): string {
    const sale = this.lot?.sale;
    if (!sale) {
      return '';
    }
    const parts: string[] = [];
    const range = this.buildDateRange(intlLocale, sale.starts_on, sale.ends_on);
    if (range) {
      parts.push(range);
    }
    const venue = resolveTranslation(sale.venue, this.locale);
    if (venue) {
      parts.push(venue);
    }
    return parts.join(' · ');
  }

  private buildDateRange(
    intlLocale: string,
    startsOn: string | null,
    endsOn: string | null
  ): string {
    const start = startsOn ? parseIsoDate(startsOn) : Number.NaN;
    const end = endsOn ? parseIsoDate(endsOn) : Number.NaN;
    const hasStart = Number.isFinite(start);
    const hasEnd = Number.isFinite(end);

    if (!hasStart && !hasEnd) {
      return '';
    }
    if (!hasStart) {
      return dateFormatter(intlLocale).format(end);
    }
    if (!hasEnd || end <= start) {
      return dateFormatter(intlLocale).format(start);
    }

    // `formatRange` collapses shared fields and picks the right separator per
    // locale. Guarded, because an engine without it would throw rather than
    // degrade.
    const formatter = dateFormatter(intlLocale);
    if (typeof formatter.formatRange === 'function') {
      try {
        return formatter.formatRange(start, end);
      } catch {
        // fall through
      }
    }
    return `${formatter.format(start)} – ${formatter.format(end)}`;
  }

  /**
   * Photos first (primary first, then the house's own order), then videos —
   * so the stage opens on the conformation shot where there is one.
   */
  private buildMedia(lot: ExternalLotView): LotMediaItem[] {
    const photos = (lot.photos ?? [])
      .slice()
      .sort(
        (a, b) =>
          Number(b.is_primary) - Number(a.is_primary) || a.order - b.order
      )
      .map<LotMediaItem>((photo) => ({
        id: `photo-${photo.id}`,
        kind: 'photo',
        url: photo.url,
        thumbUrl: photo.thumb_url || photo.url,
        caption: (photo.caption ?? '').trim(),
        ratio: this.ratioOf(photo.width, photo.height),
        video: null,
      }));

    const videos = (lot.videos ?? []).map<LotMediaItem>((video) => ({
      id: `video-${video.id}`,
      kind: 'video',
      url: video.poster_url ?? '',
      thumbUrl: video.poster_url ?? '',
      caption: (video.title ?? '').trim(),
      // `null`, not a measured value: a video plays in a 16:9 frame whatever
      // shape its poster happens to be, and `stageRatio` resolves null to the
      // default the embed needs.
      ratio: null,
      video,
    }));

    return [...photos, ...videos];
  }

  /**
   * `width / height`, or `null` if either is missing or nonsensical.
   *
   * Guarded rather than trusted: these come from `getimagesize` on a partner's
   * file, and a zero would put `aspect-ratio: Infinity` on the stage.
   */
  private ratioOf(width: number | null, height: number | null): number | null {
    if (!width || !height || width <= 0 || height <= 0) {
      return null;
    }

    return width / height;
  }

  /**
   * The §6.6 fields, in catalogue reading order, then the `raw_details`
   * passthrough. A row whose value is null or empty is SKIPPED — fifteen em
   * dashes down a withdrawn lot's table is noise, not information.
   */
  private buildDetailRows(
    lot: ExternalLotView,
    lotLabel: string,
    intlLocale: string
  ): LotDetailRow[] {
    const rows: LotDetailRow[] = [];

    const add = (
      labelKey: string,
      value: string | number | null | undefined,
      mono = false
    ): void => {
      const text = value === null || value === undefined ? '' : String(value).trim();
      if (text.length > 0) {
        rows.push({ labelKey, labelText: null, value: text, valueKey: null, mono });
      }
    };

    // The lot number's label is house DATA ("Lot" / "Hip"), not a translation.
    if (lot.lot_number) {
      rows.push({
        labelKey: lotLabel ? null : 'international_auctions.sale.lot_number',
        labelText: lotLabel || null,
        value: lot.lot_number,
        valueKey: null,
        mono: true,
      });
    }

    add('common.name', lot.display_title);
    add('international_auctions.lot.sire', lot.sire);
    add('international_auctions.lot.dam', lot.dam);
    add('international_auctions.lot.sire_of_dam', lot.sire_of_dam);
    add('international_auctions.lot.colour', lot.color);
    add('international_auctions.lot.sex', lot.sex);
    add('international_auctions.lot.sex_colour_code', lot.sex_color_code, true);
    add('international_auctions.lot.year_foaled', lot.year_foaled, true);
    add('international_auctions.lot.foaled_in', lot.foaled_in);
    // The unit rides on the LABEL rather than the value: `16.1` is 16 hands one
    // inch, so gluing "hh" onto it here would need the translate service in a
    // method that otherwise hands the template raw house data.
    add('international_auctions.lot.height', lot.height_hands, true);
    add(
      'international_auctions.lot.height_measured',
      lot.height_measured_on
        ? dateFormatter(intlLocale).format(parseIsoDate(lot.height_measured_on))
        : null
    );
    add('international_auctions.lot.horse_type', lot.horse_type);
    add('international_auctions.lot.sale_day', lot.sale_day);
    add('international_auctions.lot.session', lot.session_label);
    add('international_auctions.lot.location', this.buildLocation(lot));
    add('international_auctions.lot.stabling', lot.stabling);
    add('international_auctions.lot.covered_by', lot.covered_by);
    add('international_auctions.lot.vat_status', lot.vat_status);

    // The one field an empty value does not remove. A house that publishes a
    // declarations block and leaves it blank has said "nothing declared", which
    // is a disclosure a buyer needs; only a house that does not publish the
    // block at all (`undefined`) has said nothing.
    if (lot.declarations !== undefined) {
      const declared = (lot.declarations ?? '').trim();
      rows.push({
        labelKey: 'international_auctions.lot.declarations',
        labelText: null,
        value: declared,
        valueKey: declared
          ? null
          : 'international_auctions.lot.no_declarations',
        mono: false,
      });
    }

    // The safety net. Houses invent fields faster than we can model them, and
    // an unmapped pair still has to reach the screen.
    for (const detail of lot.raw_details ?? []) {
      const label = (detail?.label ?? '').trim();
      const value = (detail?.value ?? '').trim();
      if (label && value) {
        rows.push({
          labelKey: null,
          labelText: label,
          value,
          valueKey: null,
          mono: false,
        });
      }
    }

    return rows;
  }

  /**
   * Where the horse physically stands. The country code is appended rather than
   * given a row of its own — "GB" alone answers a question nobody asked, but
   * beside the yard it is the paperwork the buyer is really reading for.
   */
  private buildLocation(lot: ExternalLotView): string {
    const address = (lot.location_address ?? '').trim();
    const country = (lot.location_country_code ?? '').trim().toUpperCase();
    return [address, country].filter((part) => part.length > 0).join(' · ');
  }

  private isPedigreeDocument(doc: ExternalLotDocument): boolean {
    const name = (resolveTranslation(doc.category_name, 'en') ?? '')
      .trim()
      .toLowerCase();
    return PEDIGREE_CATEGORIES.has(name);
  }

  private buildDocuments(
    docs: ExternalLotDocument[],
    intlLocale: string
  ): LotDocumentRow[] {
    return docs.map((doc) => {
      const isLink = doc.is_link === true;
      const row: LotDocumentRow = {
        id: doc.id,
        category: resolveTranslation(doc.category_name, this.locale) ?? '',
        // The four file fields are filled from the active variant below, so
        // that a translated document's name, size and viewer all move together
        // when the language is switched.
        fileName: '',
        fileUrl: '',
        dateLabel: doc.file_date
          ? dateFormatter(intlLocale).format(parseIsoDate(doc.file_date))
          : '',
        sizeLabel: '',
        kind: 'other',
        // Anything other than an absent lock is a lock. See `LotDocumentRow`
        // for why `accepted_buyer` does not get a state of its own here.
        isLocked: doc.lock_type !== null,
        accessKey:
          doc.lock_type === null
            ? 'international_auctions.lot.access_free'
            : 'international_auctions.lot.access_sign_in',
        isLink,
        actionKey: isLink
          ? 'international_auctions.lot.open'
          : 'international_auctions.lot.download',
        lockedActionKey: isLink
          ? 'international_auctions.lot.sign_in_to_open'
          : 'international_auctions.lot.sign_in_to_download',
        variants: this.buildVariants(doc, isLink, intlLocale),
        activeLocale: 'en',
      };

      this.applyVariant(row, this.preferredDocumentLocale(row));
      return row;
    });
  }

  /**
   * One variant per language the document actually holds a file for.
   *
   * A document with no `files` map at all is a house feed that has never heard
   * of translation, and its flat fields ARE the single English file — one code
   * path for both, rather than a branch every caller has to remember.
   */
  private buildVariants(
    doc: ExternalLotDocument,
    isLink: boolean,
    intlLocale: string
  ): LotDocumentVariant[] {
    const variants: LotDocumentVariant[] = [];

    for (const locale of DOCUMENT_LOCALES) {
      const file = doc.files?.[locale];
      if (file && this.fileUrlOf(file).length > 0) {
        variants.push(this.toVariant(locale, file, isLink, intlLocale));
      }
    }

    if (variants.length === 0) {
      variants.push(this.toVariant('en', doc, isLink, intlLocale));
    }

    return variants;
  }

  private toVariant(
    locale: SupportedLocale,
    file: ExternalLotDocumentFile,
    isLink: boolean,
    intlLocale: string
  ): LotDocumentVariant {
    return {
      locale,
      labelKey: DOCUMENT_LOCALE_LABEL_KEYS[locale],
      fileName: file.file_name,
      fileUrl: this.fileUrlOf(file),
      // A link is never previewable, whatever its mime says: the target is
      // somebody else's portal, and it will refuse to be framed.
      kind: isLink ? 'other' : this.documentKind(file),
      // A link has no bytes, and `formatBytes` returns '' rather than `0 B`,
      // so the row simply drops the size.
      sizeLabel: this.formatBytes(file.size_bytes, intlLocale),
    };
  }

  /**
   * The address of one file, whichever of its two names it arrived under.
   *
   * Empty is the signal that this locale offers nothing to open, and it is the
   * answer for a locked document too — the API withholds the URL rather than
   * the row, so a locked file must not become a variant the toggle offers.
   */
  private fileUrlOf(file: ExternalLotDocumentFile): string {
    return (file.url ?? file.file_url ?? '').trim();
  }

  /**
   * The platform's fallback chain, applied to files: an explicit choice → the
   * visitor's language → English → whatever exists. An Arabic visitor therefore
   * reads the English pedigree while the Arabic one is still being produced,
   * rather than meeting an empty panel.
   */
  private preferredDocumentLocale(row: LotDocumentRow): SupportedLocale {
    const held = (locale: SupportedLocale): boolean =>
      row.variants.some((variant) => variant.locale === locale);

    const chosen = this.documentLocale.get(row.id);
    if (chosen && held(chosen)) {
      return chosen;
    }
    if (held(this.locale)) {
      return this.locale;
    }
    return held('en') ? 'en' : row.variants[0].locale;
  }

  /** Mirrors one variant's file onto the row the template actually reads. */
  private applyVariant(row: LotDocumentRow, locale: SupportedLocale): void {
    const variant =
      row.variants.find((candidate) => candidate.locale === locale) ??
      row.variants[0];

    row.activeLocale = variant.locale;
    row.fileName = variant.fileName;
    row.fileUrl = variant.fileUrl;
    row.kind = variant.kind;
    row.sizeLabel = variant.sizeLabel;
  }

  /**
   * PDF and image documents are shown inline; everything else can only be
   * downloaded.
   *
   * `mime` is the primary signal, but it is house-supplied and routinely
   * arrives empty or as `application/octet-stream`, so the file extension is
   * the fallback rather than an afterthought. Getting this wrong is cheap in
   * one direction only — calling a real PDF `other` costs a visitor one extra
   * click, while calling a `.docx` a PDF shows them an empty grey frame — so
   * anything unrecognised stays `other`.
   */
  private documentKind(doc: ExternalLotDocumentFile): LotDocumentKind {
    const mime = (doc.mime ?? '').trim().toLowerCase();
    if (mime === 'application/pdf') {
      return 'pdf';
    }
    if (mime.startsWith('image/')) {
      return 'image';
    }

    const name = (doc.file_name ?? '').trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    const ext = dot > -1 ? name.slice(dot + 1) : '';
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp'].includes(ext)) {
      return 'image';
    }

    return 'other';
  }

  /** Newest amendment first — the reason the timeline exists at all. */
  private buildUpdates(
    lot: ExternalLotView,
    intlLocale: string
  ): LotUpdateRow[] {
    return (lot.updates ?? [])
      .slice()
      .sort((a, b) => parseIsoDate(b.published_on) - parseIsoDate(a.published_on))
      .map((update) => ({
        id: update.id,
        body: update.body,
        params: {
          date: dateFormatter(intlLocale).format(
            parseIsoDate(update.published_on)
          ),
        },
      }));
  }

  /** Only tabs with something behind them are ever built. */
  private buildTabs(): LotTab[] {
    const tabs: LotTab[] = [];
    if (this.pedigreeDocuments.length > 0) {
      tabs.push({
        id: 'pedigree',
        labelKey: 'international_auctions.lot.tab_pedigree',
      });
    }
    if (this.videos.length > 0) {
      tabs.push({ id: 'video', labelKey: 'international_auctions.lot.tab_video' });
    }
    if (this.otherDocuments.length > 0) {
      tabs.push({
        id: 'documents',
        labelKey: 'international_auctions.lot.tab_documents',
      });
    }
    if (this.updates.length > 0) {
      tabs.push({
        id: 'updates',
        labelKey: 'international_auctions.lot.tab_updates',
      });
    }

    // The buyer's own notes are NOT a tab. Every tab here describes something
    // the house published; the notes panel is the visitor's own writing and
    // has its own section under this card. See the template.
    return tabs;
  }

  /**
   * `402 kB` / `1.2 MB`, localised where the engine supports unit formatting
   * and falling back to the international symbols where it does not.
   */
  private formatBytes(bytes: number, intlLocale: string): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '';
    }
    const inMegabytes = bytes / (1024 * 1024);
    const useMegabytes = inMegabytes >= 1;
    const value = useMegabytes ? inMegabytes : bytes / 1024;
    try {
      return new Intl.NumberFormat(intlLocale, {
        style: 'unit',
        unit: useMegabytes ? 'megabyte' : 'kilobyte',
        unitDisplay: 'short',
        maximumFractionDigits: useMegabytes ? 1 : 0,
      }).format(value);
    } catch {
      return `${Math.round(value)} ${useMegabytes ? 'MB' : 'KB'}`;
    }
  }
}
